import { createContext, useCallback, useContext, useMemo, useReducer } from "react";
import type { ReactNode } from "react";

import { limparConsultas } from "@/ds";
import { OBRA_CONSULTAS_DA_EMBARCACAO_PREFIXO } from "@/modules/obras";

import {
  createInitialNovaObraState,
  hasDadosDependentesDaEmbarcacao,
  isNovaObraDirty,
  novaObraReducer,
  validateEtapa,
} from "./nova-obra.store";
import { PRIMEIRA_ETAPA, ULTIMA_ETAPA } from "./nova-obra.types";
import type {
  DadosObraEtapa1,
  NovaObraEtapa,
  NovaObraEtapaExtra,
  NovaObraState,
} from "./nova-obra.types";

export interface NovaObraContextValue {
  state: NovaObraState;
  isDirty: boolean;
  /** Erros da etapa corrente, já filtrados para só aparecerem depois de "Próxima etapa" falhar. */
  errosVisiveis: Record<string, string>;
  hasDadosDependentesDaEmbarcacao: boolean;
  atualizarObra: (campos: Partial<DadosObraEtapa1>) => void;
  alternarManutencaoCorretiva: (ativa: boolean) => void;
  trocarEmbarcacao: (embarcacaoId: string | null) => void;
  salvarEtapa: (etapa: NovaObraEtapaExtra, dados: unknown) => void;
  /** HU 2549: guarda a justificativa das condições pendentes (limite de classe e contrato). */
  definirJustificativaCondicoes: (texto: string) => void;
  /** Valida só a etapa corrente: com erro, sinaliza os campos e permanece (retorna `false`). */
  avancar: () => boolean;
  /** Não valida e não perde dados (RN09). */
  voltar: () => void;
  irParaEtapa: (etapa: NovaObraEtapa) => void;
  reiniciar: () => void;
}

const NovaObraContext = createContext<NovaObraContextValue | null>(null);

export function NovaObraProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(novaObraReducer, undefined, createInitialNovaObraState);

  const avancar = useCallback(() => {
    if (Object.keys(validateEtapa(state, state.etapaAtual)).length > 0) {
      dispatch({ type: "mostrar-erros", etapa: state.etapaAtual });
      return false;
    }
    if (state.etapaAtual >= ULTIMA_ETAPA) return false;

    dispatch({ type: "ir-para-etapa", etapa: (state.etapaAtual + 1) as NovaObraEtapa });
    return true;
  }, [state]);

  const voltar = useCallback(() => {
    if (state.etapaAtual <= PRIMEIRA_ETAPA) return;
    dispatch({ type: "ir-para-etapa", etapa: (state.etapaAtual - 1) as NovaObraEtapa });
  }, [state.etapaAtual]);

  const value = useMemo<NovaObraContextValue>(() => {
    const visivel = state.etapasComErroVisivel.includes(state.etapaAtual);

    return {
      state,
      isDirty: isNovaObraDirty(state),
      errosVisiveis: visivel ? validateEtapa(state, state.etapaAtual) : {},
      hasDadosDependentesDaEmbarcacao: hasDadosDependentesDaEmbarcacao(state),
      atualizarObra: (campos) => dispatch({ type: "atualizar-obra", campos }),
      alternarManutencaoCorretiva: (ativa) =>
        dispatch({ type: "alternar-manutencao-corretiva", ativa }),
      // As consultas da embarcação (informações, centros de custo, limites) saem do cache: a troca as refaz.
      trocarEmbarcacao: (embarcacaoId) => {
        limparConsultas(OBRA_CONSULTAS_DA_EMBARCACAO_PREFIXO);
        dispatch({ type: "trocar-embarcacao", embarcacaoId });
      },
      salvarEtapa: (etapa, dados) => dispatch({ type: "salvar-etapa", etapa, dados }),
      definirJustificativaCondicoes: (texto) =>
        dispatch({ type: "definir-justificativa-condicoes", texto }),
      avancar,
      voltar,
      irParaEtapa: (etapa) => dispatch({ type: "ir-para-etapa", etapa }),
      reiniciar: () => dispatch({ type: "reiniciar" }),
    };
  }, [state, avancar, voltar]);

  return <NovaObraContext.Provider value={value}>{children}</NovaObraContext.Provider>;
}

export function useNovaObra(): NovaObraContextValue {
  const context = useContext(NovaObraContext);
  if (!context) throw new Error("useNovaObra deve ser usado dentro de NovaObraProvider.");
  return context;
}
